import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Api, { getApiUrl } from '../../services/api';
import { useAtozasTranslate } from '../../context/AtozasLanguageContext';
import { useTheme } from '../../context/ThemeContext';
import LanguageSelector from './LanguageSelector';
import ConfirmationModal from './ConfirmationModal';

const UDsidebar = ({ isOpen, onClose, user, driver, isDriver = false, onHome, onProfile, onRides, onLogout, onDriverTabChange, activeDriverTab, onBecomeDriver, onDriverDashboard, onUserDashboard }) => {
  const [driverProfile, setDriverProfile] = useState(driver);
  const [userProfile, setUserProfile] = useState(null); // Start with null to force fetch if needed
  const [rating, setRating] = useState({ average: 0, total: 0 });
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { darkMode, toggleTheme } = useTheme();

  const tLanguage = useAtozasTranslate('Language');
  const tEditProfile = useAtozasTranslate('Edit Profile');
  const tDashboard = useAtozasTranslate('Dashboard');
  const tMyRides = useAtozasTranslate('My Rides');
  const tEarnings = useAtozasTranslate('Earnings');
  const tAccount = useAtozasTranslate('Account');
  const tHome = useAtozasTranslate('Home');
  const tProfile = useAtozasTranslate('Profile');
  const tUserDashboard = useAtozasTranslate('User Dashboard');
  const tDriverDashboard = useAtozasTranslate('Driver Dashboard');
  const tBecomeDriver = useAtozasTranslate('Become Driver');
  const tLogout = useAtozasTranslate('Logout');

  // Fetch driver profile data if driver prop is missing profile picture
  useEffect(() => {
    const fetchDriverProfile = async () => {
      if (!isDriver || !user?.driverId || driverProfile?.profilePicture) return;
      
      try {
        const token = localStorage.getItem('uttaapp_token');
        const response = await axios.get(Api.Driver.profile(user.driverId), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success && response.data.driver) {
          setDriverProfile(response.data.driver);
        }
      } catch (error) {
        console.error('Error fetching driver profile for sidebar:', error);
      }
    };

    if (isOpen && isDriver) {
      fetchDriverProfile();
    }
  }, [isOpen, isDriver, user?.driverId, driverProfile?.profilePicture]);

  // Fetch user profile data if user prop is missing profile picture
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (isDriver || !isOpen) return;
      
      // Check if profile picture already exists in user prop
      if (user?.profilePicture) {
        console.log('User profile picture exists in prop:', user.profilePicture);
        // Update userProfile with user prop if it has profile picture
        if (!userProfile || userProfile.profilePicture !== user.profilePicture) {
          setUserProfile(user);
        }
        return;
      }
      
      // If userProfile already has profile picture, don't fetch again
      if (userProfile?.profilePicture) {
        console.log('User profile picture already fetched:', userProfile.profilePicture);
        return;
      }
      
      try {
        const token = localStorage.getItem('uttaapp_token');
        if (!token) {
          console.log('No token found for user profile fetch');
          return;
        }
        
        console.log('Fetching user profile for sidebar...');
        const response = await axios.get(Api.User.profile, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        console.log('User profile API response:', response.data);
        if (response.data.success && response.data.user) {
          console.log('User profile fetched:', response.data.user);
          console.log('User profile picture:', response.data.user.profilePicture);
          setUserProfile(response.data.user);
        }
      } catch (error) {
        console.error('Error fetching user profile for sidebar:', error);
      }
    };

    fetchUserProfile();
  }, [isOpen, isDriver, user, userProfile]);

  // Fetch rating (driver or user)
  useEffect(() => {
    const fetchRating = async () => {
      if (!isOpen) return;
      
      try {
        const token = localStorage.getItem('uttaapp_token');
        if (!token) {
          setRating({ average: 0, total: 0 });
          return;
        }

        if (isDriver && user?.driverId) {
          // Fetch driver rating
          const response = await axios.get(Api.Driver.rating, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.data.success) {
            setRating({
              average: response.data.averageRating || 0,
              total: response.data.totalRatings || 0
            });
          } else {
            setRating({ average: 0, total: 0 });
          }
        } else if (!isDriver && (user?.id || user?._id)) {
          // Fetch user rating
          const userId = user?.id || user?._id;
          const response = await axios.get(Api.Rating.getUserRatings(userId), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.data.success) {
            setRating({
              average: parseFloat(response.data.averageRating) || 0,
              total: response.data.totalRatings || 0
            });
          } else {
            setRating({ average: 0, total: 0 });
          }
        } else {
          setRating({ average: 0, total: 0 });
        }
      } catch (error) {
        console.error('Error fetching rating:', error);
        setRating({ average: 0, total: 0 });
      }
    };

    fetchRating();
  }, [isOpen, isDriver, user?.driverId, user?.id, user?._id]);

  // Render stars component
  // CRITICAL: Use notranslate to prevent Google Translate from affecting star display
  const renderStars = (ratingValue) => {
    const stars = [];
    const fullStars = Math.floor(ratingValue);
    const hasHalfStar = ratingValue % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <span key={i} className="text-yellow-400 text-lg notranslate" translate="no">★</span>
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <span key={i} className="text-yellow-400 text-lg notranslate" translate="no">☆</span>
        );
      } else {
        stars.push(
          <span key={i} className="text-gray-300 text-lg notranslate" translate="no">☆</span>
        );
      }
    }
    return stars;
  };

  if (!isOpen) return null;

  // Use fetched profiles if available, otherwise use props
  const currentDriver = driverProfile || driver;
  const currentUser = userProfile || user; // Use fetched profile or fallback to prop

  // Get user name
  const userName = currentUser?.username || currentUser?.name || 'User';

  // Get driver name
  const driverName = currentDriver?.name || currentUser?.username || currentUser?.name || 'Driver';

  // Helper function to get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Construct backend URL using centralized API helper
    const apiUrl = getApiUrl();
    
    // Remove leading slash if present to avoid double slashes
    const cleanPath = imagePath.replace(/^\/+/, '').replace(/\\/g, '/');
    
    return `${apiUrl}/${cleanPath}`;
  };

  // Get profile picture URL - check multiple possible field names
  const profilePictureUrl = isDriver 
    ? getImageUrl(currentDriver?.profilePicture || currentDriver?.profile_picture || currentDriver?.photo)
    : getImageUrl(currentUser?.profilePicture || currentUser?.profile_picture || currentUser?.photo);

  // Debug logging
  if (!isDriver) {
    console.log('Sidebar User Profile Debug:', {
      user: user,
      userProfile: userProfile,
      currentUser: currentUser,
      profilePicture: currentUser?.profilePicture,
      profile_picture: currentUser?.profile_picture,
      photo: currentUser?.photo,
      profilePictureUrl: profilePictureUrl
    });
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[9998] bg-slate-950/52 md:hidden"
        onClick={onClose}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      ></div>

      {/* Sidebar */}
      <div 
        className="fixed left-0 top-0 z-[9999] h-full w-[84vw] max-w-[332px] overflow-y-auto rounded-r-[28px] bg-[#f7f8f8] shadow-[0_22px_50px_-18px_rgba(15,23,42,0.48)] md:hidden dark:bg-[#0f1720]" 
        style={{ 
          maxHeight: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          overflowY: 'auto',
          paddingTop: 'max(12px, env(safe-area-inset-top))',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))'
        }}
      >
        {/* Header */}
        <div className="mx-3 overflow-hidden rounded-[24px] bg-[#0f766e] text-white shadow-[0_16px_34px_-24px_rgba(15,118,110,0.7)]">
          <div className="flex items-center justify-between px-4 pb-3 pt-4">
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-white/70">
                {isDriver ? 'Driver Space' : 'Rider Space'}
              </div>
              <div className="mt-1 text-lg font-semibold">UttaApp</div>
            </div>
            <button
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/12 text-white transition-colors hover:bg-white/20"
              aria-label="Close menu"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mx-3 mb-3 rounded-[22px] bg-white/12 px-4 py-4 backdrop-blur-sm">
            <div className="flex items-center gap-3.5">
              {/* Profile Picture */}
              <div className="relative flex h-[68px] w-[68px] flex-shrink-0 items-center justify-center overflow-hidden rounded-[22px] bg-white/16 text-xl font-semibold">
                {profilePictureUrl ? (
                  <img 
                    src={profilePictureUrl} 
                    alt={isDriver ? driverName : userName}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) {
                        fallback.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div 
                  className="profile-fallback h-full w-full items-center justify-center bg-white/10"
                  style={{ display: profilePictureUrl ? 'none' : 'flex' }}
                >
                  {isDriver 
                    ? driverName.charAt(0).toUpperCase()
                    : userName.charAt(0).toUpperCase()
                  }
                </div>
              </div>
              
              {/* Name, Rating Stars, and Edit Profile */}
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-medium text-white/72">
                  {isDriver ? 'Active driver account' : 'Personal account'}
                </div>
                <h3 className="mt-1 truncate text-[21px] font-semibold text-white">
                  {isDriver ? driverName : userName}
                </h3>
                {rating.average > 0 && (
                  <div className="notranslate mt-2 flex items-center gap-1" translate="no" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                    {renderStars(rating.average)}
                    {rating.total > 0 && (
                      <span className="notranslate ml-1 text-xs text-white/70" translate="no" style={{ whiteSpace: 'nowrap' }}>
                        ({rating.total})
                      </span>
                    )}
                  </div>
                )}
                <button
                  onClick={() => {
                    if (isDriver && onDriverTabChange) {
                      onDriverTabChange('account');
                    } else if (onProfile) {
                      onProfile();
                    }
                    onClose();
                  }}
                  className="mt-3 inline-flex items-center rounded-full bg-white px-3.5 py-1.5 text-sm font-medium text-[#0f766e] shadow-sm transition hover:bg-slate-100"
                >
                  {tEditProfile}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="space-y-4 px-3 pb-3 pt-4">
          <div className="rounded-[22px] bg-white px-4 py-3 shadow-[0_12px_28px_-24px_rgba(15,23,42,0.32)] ring-1 ring-[#e7edeb] dark:bg-slate-800 dark:ring-slate-700">
            <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
              Quick Controls
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 rounded-[18px] bg-[#f5f7f7] px-3 py-3 dark:bg-slate-900">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-100">{tLanguage}</div>
                  <div className="text-xs text-slate-400 dark:text-slate-500">Choose your preferred app language</div>
                </div>
                <LanguageSelector
                  aria-label={tLanguage}
                  title={tLanguage}
                  variant="pill"
                  showSelectedCode
                  className="shrink-0 border-[#d9e5e0] bg-white px-2.5 py-2 text-slate-700 shadow-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              <button
                onClick={toggleTheme}
                className="flex w-full items-center justify-between rounded-[18px] bg-[#f5f7f7] px-3 py-3 text-left transition-colors hover:bg-[#eef3f1] dark:bg-slate-900 dark:hover:bg-slate-950"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-white text-slate-600 shadow-sm dark:bg-slate-800 dark:text-slate-200">
                    {darkMode ? (
                      <svg className="h-[18px] w-[18px] text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-[18px] w-[18px]" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                      </svg>
                    )}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-100">Appearance</div>
                    <div className="text-xs text-slate-400 dark:text-slate-500">{darkMode ? 'Light mode enabled' : 'Dark mode disabled'}</div>
                  </div>
                </div>
                <span className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                  darkMode ? 'bg-[#0f766e]' : 'bg-slate-300'
                }`}>
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    darkMode ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </span>
              </button>
            </div>
          </div>

          {isDriver ? (
            <>
              <div className="px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                Driver Menu
              </div>
              <div className="space-y-1.5 rounded-[22px] bg-white p-2 shadow-[0_12px_28px_-24px_rgba(15,23,42,0.32)] ring-1 ring-[#e7edeb] dark:bg-slate-800 dark:ring-slate-700">
              <button
                onClick={() => {
                  if (onDriverTabChange) onDriverTabChange('dashboard');
                  onClose();
                }}
                className={`flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left transition-colors ${
                  activeDriverTab === 'dashboard'
                    ? 'bg-[#e9f7f2] text-[#0f766e] dark:bg-primary-900/30 dark:text-primary-400'
                    : 'text-gray-700 hover:bg-[#f5f8f7] dark:text-gray-200 dark:hover:bg-gray-700/60'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${
                    activeDriverTab === 'dashboard' ? 'bg-white/85 text-[#0f766e]' : 'bg-[#f3f6f5] text-slate-600 dark:bg-slate-900 dark:text-slate-200'
                  }`}>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </span>
                  <span className="text-[15px] font-medium">{tDashboard}</span>
                </span>
                <svg className="h-5 w-5 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => {
                  if (onDriverTabChange) onDriverTabChange('rides');
                  onClose();
                }}
                className={`flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left transition-colors ${
                  activeDriverTab === 'rides'
                    ? 'bg-[#e9f7f2] text-[#0f766e] dark:bg-primary-900/30 dark:text-primary-400'
                    : 'text-gray-700 hover:bg-[#f5f8f7] dark:text-gray-200 dark:hover:bg-gray-700/60'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${
                    activeDriverTab === 'rides' ? 'bg-white/85 text-[#0f766e]' : 'bg-[#f3f6f5] text-slate-600 dark:bg-slate-900 dark:text-slate-200'
                  }`}>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </span>
                  <span className="text-[15px] font-medium">{tMyRides}</span>
                </span>
                <svg className="h-5 w-5 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => {
                  if (onDriverTabChange) onDriverTabChange('earnings');
                  onClose();
                }}
                className={`flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left transition-colors ${
                  activeDriverTab === 'earnings'
                    ? 'bg-[#e9f7f2] text-[#0f766e] dark:bg-primary-900/30 dark:text-primary-400'
                    : 'text-gray-700 hover:bg-[#f5f8f7] dark:text-gray-200 dark:hover:bg-gray-700/60'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${
                    activeDriverTab === 'earnings' ? 'bg-white/85 text-[#0f766e]' : 'bg-[#f3f6f5] text-slate-600 dark:bg-slate-900 dark:text-slate-200'
                  }`}>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  <span className="text-[15px] font-medium">{tEarnings}</span>
                </span>
                <svg className="h-5 w-5 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => {
                  if (onDriverTabChange) onDriverTabChange('account');
                  onClose();
                }}
                className={`flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left transition-colors ${
                  activeDriverTab === 'account'
                    ? 'bg-[#e9f7f2] text-[#0f766e] dark:bg-primary-900/30 dark:text-primary-400'
                    : 'text-gray-700 hover:bg-[#f5f8f7] dark:text-gray-200 dark:hover:bg-gray-700/60'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${
                    activeDriverTab === 'account' ? 'bg-white/85 text-[#0f766e]' : 'bg-[#f3f6f5] text-slate-600 dark:bg-slate-900 dark:text-slate-200'
                  }`}>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </span>
                  <span className="text-[15px] font-medium">{tAccount}</span>
                </span>
                <svg className="h-5 w-5 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              </div>
            </>
          ) : (
            <>
              <div className="px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                Menu
              </div>
              <div className="space-y-1.5 rounded-[22px] bg-white p-2 shadow-[0_12px_28px_-24px_rgba(15,23,42,0.32)] ring-1 ring-[#e7edeb] dark:bg-slate-800 dark:ring-slate-700">
              <button
                onClick={() => {
                  if (onHome) onHome();
                  onClose();
                }}
                className="flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-gray-700 transition-colors hover:bg-[#f5f8f7] dark:text-gray-200 dark:hover:bg-gray-700/60"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#f3f6f5] text-slate-600 dark:bg-slate-900 dark:text-slate-200">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </span>
                  <span className="text-[15px] font-medium">{tHome}</span>
                </span>
                <svg className="h-5 w-5 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => {
                  if (onRides) onRides();
                  onClose();
                }}
                className="flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-gray-700 transition-colors hover:bg-[#f5f8f7] dark:text-gray-200 dark:hover:bg-gray-700/60"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#f3f6f5] text-slate-600 dark:bg-slate-900 dark:text-slate-200">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </span>
                  <span className="text-[15px] font-medium">{tMyRides}</span>
                </span>
                <svg className="h-5 w-5 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => {
                  if (onProfile) onProfile();
                  onClose();
                }}
                className="flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-gray-700 transition-colors hover:bg-[#f5f8f7] dark:text-gray-200 dark:hover:bg-gray-700/60"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#f3f6f5] text-slate-600 dark:bg-slate-900 dark:text-slate-200">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </span>
                  <span className="text-[15px] font-medium">{tProfile}</span>
                </span>
                <svg className="h-5 w-5 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              </div>
            </>
          )}

          {/* Driver/User Dashboard Switch Options */}
          {isDriver ? (
            // Driver Dashboard - Show User Dashboard option
            onUserDashboard && (
              <div className="rounded-[22px] bg-white p-2 shadow-[0_12px_28px_-24px_rgba(15,23,42,0.32)] ring-1 ring-[#e7edeb] dark:bg-slate-800 dark:ring-slate-700">
                <button
                  onClick={() => {
                    if (onUserDashboard) onUserDashboard();
                    onClose();
                  }}
                  className="flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-gray-700 transition-colors hover:bg-[#f5f8f7] dark:text-gray-200 dark:hover:bg-gray-700/60"
                >
                  <span className="flex items-center gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#f3f6f5] text-slate-600 dark:bg-slate-900 dark:text-slate-200">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </span>
                    <span className="text-[15px] font-medium">{tUserDashboard}</span>
                  </span>
                  <svg className="h-5 w-5 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )
          ) : (
            // User Dashboard - Show Become Driver or Driver Dashboard option
            <>
              {user?.driverId ? (
                // Already registered as driver - Show Driver Dashboard
                onDriverDashboard && (
                  <div className="rounded-[22px] bg-[linear-gradient(135deg,#ecfdf5_0%,#d1fae5_100%)] p-2 shadow-[0_12px_28px_-24px_rgba(15,118,110,0.22)] ring-1 ring-[#c9eadc] dark:bg-green-900/20 dark:ring-green-800">
                    <button
                      onClick={() => {
                        if (onDriverDashboard) onDriverDashboard();
                        onClose();
                      }}
                      className="flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-green-700 transition-colors hover:bg-white/40 dark:text-green-400 dark:hover:bg-white/5"
                    >
                      <span className="flex items-center gap-3">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/75 text-green-700 dark:bg-white/10 dark:text-green-400">
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </span>
                        <span className="text-[15px] font-medium">{tDriverDashboard}</span>
                      </span>
                      <svg className="h-5 w-5 text-green-500/60 dark:text-green-300/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )
              ) : (
                // Not registered - Show Become Driver
                onBecomeDriver && (
                  <div className="rounded-[22px] bg-[linear-gradient(135deg,#ecfeff_0%,#cffafe_100%)] p-2 shadow-[0_12px_28px_-24px_rgba(14,165,233,0.2)] ring-1 ring-[#cceff7] dark:bg-cyan-900/20 dark:ring-cyan-800">
                    <button
                      onClick={() => {
                        if (onBecomeDriver) onBecomeDriver();
                        onClose();
                      }}
                      className="flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-primary-600 transition-colors hover:bg-white/45 dark:text-primary-400 dark:hover:bg-white/5"
                    >
                      <span className="flex items-center gap-3">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/80 text-primary-600 dark:bg-white/10 dark:text-primary-400">
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </span>
                        <span className="text-[15px] font-medium">{tBecomeDriver}</span>
                      </span>
                      <svg className="h-5 w-5 text-primary-500/60 dark:text-primary-300/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )
              )}
            </>
          )}

          {/* Logout Button */}
          <div className="rounded-[22px] bg-white p-2 shadow-[0_12px_28px_-24px_rgba(15,23,42,0.32)] ring-1 ring-[#e7edeb] dark:bg-slate-800 dark:ring-slate-700">
            <button
              onClick={() => {
                setShowLogoutConfirm(true);
              }}
              className="flex w-full items-center justify-between rounded-[18px] px-3.5 py-3 text-left text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
            >
              <span className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </span>
                <span className="text-[15px] font-medium">{tLogout}</span>
              </span>
              <svg className="h-5 w-5 text-red-300 dark:text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        show={showLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          if (onLogout) onLogout();
          onClose();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
};

export default UDsidebar;

